import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'
import parse from 'autosuggest-highlight/parse'
import match from 'autosuggest-highlight/match'
import CircularProgress from '@mui/material/CircularProgress';
import { useLazyQuery } from "@apollo/client";
import { GLOBAL_SEARCH } from "graphql/service";
import { Container, StyledInput, StyledList, StyledItem } from './styles'
import { debounce } from 'lodash-es';
import { debouncedErrorMessage } from 'utils/messageUtil';

const SearchBar = ({additionalstyle}) => {

  const navigate = useNavigate();
  
  const [ isActive, setIsActive ] = useState(false)
  const [ keyword, setKeyword ] = useState('')

  const [globalSearch, { loading: globalSearchLoading, data: searchData, error}] = useLazyQuery(GLOBAL_SEARCH, {
    fetchPolicy: "network-only"
  });

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        if (!value.trim()) return
        globalSearch({
          variables: {
            input: {
              query: value
            }
          }
        })
      }, 200),
    [globalSearch]
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const options = useMemo(() => {
    const results = searchData?.globalSearch
    if (!Array.isArray(results) || !keyword) return []

    const lowerCase = keyword.toLowerCase()

  return results.flatMap((result) =>
    result.items.map((item) => {
      const matches = match(item.matchedValue ?? '', lowerCase, {
        insideWords: true,
        findAllOccurrences: true
      })

      return {
        ...item,
        highlightedValue: parse(item.matchedValue ?? '', matches)
      }
    })
  )
  }, [searchData, keyword])

  
  const inputHandler = (value) => {
    setKeyword(value)
    setIsActive(true)

    if (!value.trim()) {
      setIsActive(false)
      return
    }

    debouncedSearch(value)
  }

  const handleKeyDown = (e) => {
    if(e.key === 'Enter') {
      navigate('/search-results', {state: {keyword: keyword}})
    }
  }
  
  return (
    <Container
      additionalstyle={additionalstyle}
    >
      <StyledInput
        placeholder='Search...'
        onBlur={()=>{
          setKeyword('')
          setIsActive(false)
        }}
        onKeyDown={handleKeyDown}
        value={keyword}
        onChange={(e) => inputHandler(e.target.value)}
      />
      {globalSearchLoading &&
        <CircularProgress
          style={{position: 'absolute',top: '11px', right: '140px'}}
          color="success"
          thickness={10}
          size={20}
        />
      }
      {isActive && !globalSearchLoading && options.length > 0 &&
        <StyledList>
          {
            options.map((item)=> (
              <StyledItem
                key={item.entityId}
                onClick={()=>{
                  switch (item.entityType) {
                    case 'BOOKING':
                    case 'IDENTITY_SCAN':
                    case 'PAYMENT_CARD':
                    case 'TRANSACTION':
                    case 'TENANT_PARKING':
                      if(item.relatedEntities.length > 0) {
                        const group = item.relatedEntities.find((entity) => entity.entityType === 'GROUP')
                        group.id && navigate(`/booking-details/${group.id}`)
                      }
                      break
                    case 'GROUP':
                      navigate(`/booking-details/${item.entityId}`)
                      break
                    case 'USER':
                      navigate(`/tenant-details/${item.entityId}`)
                      break
                    case 'ROOM':
                      navigate(`/room-details/${item.entityId}`)
                      break
                    default:
                      break
                  }
                  setKeyword('')
                  setIsActive(false)
                }}
              >
                <span className="type">{item.entityType}: </span>
                {item.highlightedValue.map((str, index) => (
                  <span
                    key={index}
                    className={str.highlight? 'highlight': null}
                  >
                    {str.text}
                  </span>
                ))}
              </StyledItem>
            ))
          }
        </StyledList>
      }
    </Container>
  )
}

export default SearchBar;